import { useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Section, stateColor, signed, deltaColor } from "./primitives";

const RANGES = [
  { key: "7D", days: 7, needs: 2 },
  { key: "30D", days: 30, needs: 8 },
  { key: "90D", days: 90, needs: 31 },
  { key: "1Y", days: 365, needs: 91 },
  { key: "ALL", days: null as number | null, needs: 2 },
];

const EventDot = (props: any) => {
  const { cx, cy, payload, events } = props;
  if (cx == null || cy == null || !events[payload?.date]) return null;
  return (
    <g data-testid={`li-event-marker-${payload.date}`}>
      <circle cx={cx} cy={cy} r={7} fill="#05D1E5" opacity={0.16} />
      <circle cx={cx} cy={cy} r={3.4} fill="#0b0c10" stroke="#05D1E5" strokeWidth={1.5} />
    </g>
  );
};

const HistTooltip = ({ active, payload, events, threshold }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const ev = events[p.date];
  return (
    <div className="rounded-lg border border-white/10 bg-[#0d0e14] px-3 py-2.5 text-xs">
      <p className="nl-mono text-[10px] text-gray-400">{p.date}</p>
      <p className="nl-sg text-[15px] font-bold text-white mt-0.5">
        {p.value}<span className="nl-mono text-[9px] text-gray-500 ml-1">/100</span>
        <span className="nl-mono text-[9px] font-bold uppercase ml-2" style={{ color: stateColor(p.label) }}>
          {p.label}
        </span>
      </p>
      {ev && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10">
          <p className="nl-mono text-[9px] uppercase tracking-widest text-[#05D1E5]">
            Index event · |Δ| ≥ {threshold}
          </p>
          <p className="text-[11px] text-gray-300 mt-0.5">
            {ev.prev_value ?? "—"} → {ev.value}
            <span className="ml-1.5 font-bold" style={{ color: deltaColor(ev.delta) }}>{signed(ev.delta)} pts</span>
          </p>
          {ev.state_change && (
            <p className="text-[11px] text-gray-400">State: {ev.prev_label} → {ev.label}</p>
          )}
          {ev.driver && <p className="text-[11px] text-gray-400">Top driver: {ev.driver}</p>}
        </div>
      )}
    </div>
  );
};

export const HistoryChart = ({ history, events, threshold }: {
  history: any[]; events: any[]; threshold: number;
}) => {
  const span = useMemo(() => {
    if (history.length < 2) return history.length;
    const a = new Date(history[0].date).getTime();
    const b = new Date(history[history.length - 1].date).getTime();
    return Math.round((b - a) / 86400000) + 1;
  }, [history]);
  const enabled = (r: typeof RANGES[number]) => history.length >= 2 && span >= r.needs;
  const [range, setRange] = useState(() =>
    enabled(RANGES[1]) ? "30D" : enabled(RANGES[0]) ? "7D" : "ALL");

  const eventMap = useMemo(() => {
    const m: Record<string, any> = {};
    for (const e of events) m[e.date] = e;
    return m;
  }, [events]);

  const rows = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    if (!r?.days) return history;
    const cutoff = new Date(Date.now() - r.days * 86400000).toISOString().slice(0, 10);
    return history.filter((h) => h.date >= cutoff);
  }, [history, range]);

  return (
    <Section num="03" title="Layer Index History" sub={`${history.length} recorded days`}
      testid="li-history-section">
      <div className="flex flex-wrap items-center gap-1.5 mb-5" data-testid="li-range-controls">
        {RANGES.map((r) => {
          const ok = enabled(r);
          return (
            <button key={r.key} type="button" disabled={!ok}
              onClick={() => ok && setRange(r.key)}
              data-testid={`li-range-${r.key.toLowerCase()}`}
              title={ok ? undefined : "Unlocks once enough recorded Layer Index history exists"}
              className={`nl-mono text-[10.5px] font-bold px-3 py-1.5 rounded-full border transition-colors ${
                range === r.key
                  ? "border-[#00E5FF]/60 bg-[#00E5FF]/[0.09] text-white"
                  : ok
                    ? "border-white/[0.1] text-gray-400 hover:border-white/25 hover:text-white"
                    : "border-white/[0.05] text-gray-700 cursor-not-allowed"}`}>
              {r.key}
            </button>
          );
        })}
      </div>
      <div className="h-64 sm:h-72" data-testid="li-history-chart">
        {rows.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="li-hist-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false}
                tickFormatter={(v: string) => v.slice(5)} interval="preserveStartEnd" minTickGap={30} />
              <YAxis domain={[0, 100]} stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false}
                ticks={[0, 20, 40, 60, 80, 100]} />
              <ReferenceLine y={40} stroke="#ffffff14" strokeDasharray="4 4" />
              <ReferenceLine y={60} stroke="#ffffff14" strokeDasharray="4 4" />
              <Tooltip content={<HistTooltip events={eventMap} threshold={threshold} />} />
              <Area type="monotone" dataKey="value" stroke="#A78BFA" strokeWidth={2}
                fill="url(#li-hist-fill)" dot={<EventDot events={eventMap} />}
                activeDot={{ r: 3.5, fill: "#00E5FF" }} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-gray-500 pt-20 text-center">
            History builds up daily — check back tomorrow.
          </p>
        )}
      </div>
      <p className="nl-mono text-[9.5px] text-gray-600 mt-3 leading-relaxed">
        Cyan markers flag index events — day-over-day moves of ≥ {threshold} pts. Disabled ranges unlock
        as real history accrues; nothing is backfilled or estimated.
      </p>
    </Section>
  );
};
